// PrimitiveRegistry — primitive kind → GLSL source + arg schema + layer-wrapper template.
// This is the GLSL-bearing registry; MaterialFuncRegistry (the recipe catalogue) stays GLSL-free.
// A layer = ONE primitive instance; its wrapper emits `return vec4(color, height)`.
import {
	RAMP_MAX_STOPS, isParamRef,
	type LayerArg, type ParamColor, type ParamKind, type ParamRamp, type PrimitiveKind,
} from "./@MaterialTypes.js";
import {HASH_GLSL} from "./Primitives/Hash.glsl.js";
import {RAMP_GLSL} from "./Primitives/Ramp.glsl.js";
import {FBM_GLSL} from "./Primitives/Fbm.glsl.js";
import {SCATTER_GLSL} from "./Primitives/Scatter.glsl.js";
import {WORLEY_GLSL} from "./Primitives/Worley.glsl.js";
import {WAVES_GLSL} from "./Primitives/Waves.glsl.js";
import {BANDED_NOISE_GLSL} from "./Primitives/BandedNoise.glsl.js";
import {COVERAGE_FIELD_GLSL} from "./Primitives/CoverageField.glsl.js";
import {DOMAIN_REPEAT_GLSL} from "./Primitives/DomainRepeat.glsl.js";
import {MASK_GLSL} from "./Primitives/Mask.glsl.js";
import {AMBIENT_DETAIL_GLSL} from "./Primitives/AmbientDetail.glsl.js";
import {MACRO_MOD_GLSL} from "./Primitives/MacroMod.glsl.js";

/** Per-arg schema entry. type "int" = baked integer literal (mode/shape enums — never uniform-backed);
 *  scalar = uniform-able float; color = vec3; ramp = stop array. */
export interface PrimitiveArgSchema {
	type: ParamKind | "int";
	min?: number;
	max?: number;
	default: LayerArg;
}
export type PrimitiveArgs = {[argKey: string]: string};

export interface PrimitiveDef {
	kind: PrimitiveKind;
	/** True when the primitive's GLSL exists; unimplemented kinds (waves/domainWarp/...) throw at assemble. */
	implemented: boolean;
	/** True when a MaterialLayer may use this primitive. hash/ramp are internal (composed inside other primitives). */
	layerUsable: boolean;
	argSchema: {[argKey: string]: PrimitiveArgSchema};
	/** Shared GLSL prelude fragment for this primitive ("" when unimplemented). */
	glsl: string;
	/** JS template: (args, specified, raw) → wrapper body emitting `return vec4(color, height);`.
	 *  `args` = rendered GLSL arg expressions (refs → uniform names, literals baked); `specified`
	 *  tells e.g. absent colorRamp (→ grayscale) from empty (→ black); `raw` = the UNRENDERED
	 *  arg values (Set3 §4.2: lets a layer body distinguish a literal ramp — JS-bake its mean —
	 *  from a ParamRef ramp — compute the mean in-shader so slider changes track + the D10
	 *  free-variant property holds). */
	layerBody: (args: PrimitiveArgs, specified: Set<string>, raw?: Record<string, LayerArg>) => string;
	/** Set11 Phase 1 — optional analytic-derivative body: the assembler emits `D_<recipeIdx>_<layerIdx>` from it (absent → synthesised layer-local finite difference, correct but band-limited for that one layer).  The body must produce `vec2 dh` (per-metre gradient) + `float h` (height) and `return vec3(dh, h);`; it runs inside `D_<r>_<l>(vec2 p)` and may call the layer wrapper (name passed as the 4th arg — waves reuses its value channel). */
	derivBody?: (args: PrimitiveArgs, specified: Set<string>, raw?: Record<string, LayerArg>, layerFnName?: string) => string;
	/** Set11 Phase 2 — optional GLSL expression for the layer's SUB-PIXEL slope variance (units: slope^2): the mean-square slope of the octaves/features FINER than ~2px that the gradient path cannot represent.  Summed across layers by `varChain_N` (variances of independent fields add). */
	varianceBody?: (args: PrimitiveArgs, specified: Set<string>, raw?: Record<string, LayerArg>) => string;
}

function missingBody(kind: string): (args: PrimitiveArgs, specified: Set<string>, raw?: Record<string, LayerArg>) => string {
	return (): string=>{
		throw new Error(`[PrimitiveRegistry] primitive "${kind}" is not implemented yet (Phase 8); cannot assemble a layer using it.`);
	};
}

/** PIXEL-LOCK (2026-08-13, the starfield): a length arg expressed in PIXELS instead of metres.
 *  `scaleSpace` 0 (default) returns the expression UNCHANGED — so every pre-existing recipe's
 *  emitted GLSL stays byte-identical (the D1 program-cache invariance) — and 1 multiplies it by
 *  matPixelSize() (= u_metersPerPixel), giving a screen-constant lattice: the feature keeps the
 *  same on-screen size at every zoom, which is what makes a starfield read as a starfield from
 *  "whole world" down to "single room".  Panning at a fixed zoom keeps features world-ANCHORED
 *  (the cell size is constant), so only a zoom CHANGE reshuffles the pattern.
 *  `scaleSpace` is an INT arg (a baked literal, never uniform-backed) precisely so this
 *  substitution can happen in JS codegen rather than needing a new GLSL signature param. */
function scaledLen(expr: string, scaleSpaceLit: string): string {
	return parseInt(scaleSpaceLit, 10) == 1 ? `(${expr} * matPixelSize())` : expr;
}

export const PRIMITIVE_REGISTRY: Record<PrimitiveKind, PrimitiveDef> = {
	hash: {
		kind: "hash", implemented: true, layerUsable: false, argSchema: {}, glsl: HASH_GLSL,
		layerBody: missingBody("hash"),
	},
	ramp: {
		kind: "ramp", implemented: true, layerUsable: false, argSchema: {}, glsl: RAMP_GLSL,
		layerBody: missingBody("ramp"),
	},
	fbm: {
		kind: "fbm", implemented: true, layerUsable: true, glsl: FBM_GLSL,
		argSchema: {
			scale: {type: "scalar", min: 0.05, max: 1000, default: 6},
			octaves: {type: "scalar", min: 1, max: 8, default: 5},
			lacunarity: {type: "scalar", min: 1.001, max: 8, default: 2},
			persistence: {type: "scalar", min: 0.05, max: 0.95, default: 0.5},
			mode: {type: "int", min: 0, max: 3, default: 0},
			colorRamp: {type: "ramp", default: []},
			heightScale: {type: "scalar", min: 0, max: 10, default: 0},
			// 2026-08-13 — PIXEL-LOCK: 0 = `scale` is in METRES (default; byte-identical GLSL),
			// 1 = in PIXELS (scale * matPixelSize() → a screen-constant lattice).  See scaledLen.
			scaleSpace: {type: "int", min: 0, max: 1, default: 0},
			// Option D — a SHIFT to the ramp lookup t AND the sub-pixel fade's mean TARGET (rampMeanShifted).  Normally 0
			// (exact no-op, byte-identical GLSL); point it at a ModRef and the colour slides along its ramp as the macro
			// modulator varies — a shifted TARGET is a function of position, so it (unlike a constant) survives zoom-out.
			colorShift: {type: "scalar", min: -1, max: 1, default: 0},
		},
		layerBody: (a, specified, raw)=>{
			// Color remap per mode (fbmVal returns the raw noise value; ramp t wants [0,1]):
			// mode 0 (fbm): [-1,1] → [0,1]; mode 1 (ridged)/3 (turbulence): already [0,1];
			// mode 2 (billow): [-1,0] → [0,1].
			const mode = parseInt(a.mode, 10);
			const remap = mode == 0 ? "t = t * 0.5 + 0.5;" : (mode == 2 ? "t = t + 1.0;" : "");
			const scale = scaledLen(a.scale, a.scaleSpace);
			// Set3 §4.2 — the layer-scale sub-pixel fade converges to the ramp's MEAN colour (a t=0.5
			// sample is NOT the mean; the zoomed-out wash fix, matching scatter/worley/bandedNoise).
			const baseMeanExpr = specified.has("colorRamp")
				? meanFadeExpr(raw!.colorRamp, a.colorRamp)
				: "vec3(0.5)";
			// Option D — with a colorShift the fade target must be the CONDITIONAL mean (rampMeanShifted): shifting only the
			// per-fragment rampVal(t) while leaving the target constant would fade the modulation out exactly where it is the
			// only thing left.  `shift` is a statement so the lookup and target read one consistent value.
			const shiftIsNoop = a.colorShift == "0.0";
			const meanExpr = (specified.has("colorRamp") && !shiftIsNoop)
				? `rampMeanShifted(${a.colorRamp}, ${baseMeanExpr}, shift)`
				: baseMeanExpr;
			const shiftStmt = shiftIsNoop ? "" : `float shift = ${a.colorShift};\n  `;
			const tShift = shiftIsNoop ? "" : "\n  t = clamp(t + shift, 0.0, 1.0);";
			const colorPart = specified.has("colorRamp")
				// Layer-scale band-limit — fade to the ramp MEAN below ~1 px per cell (matching
				// fbmVal's analytic clamp cell/px so the layer fade is never the over-aggressive
				// binding constraint; still rampMean-converging, not rampVal(0.5)).
				? `float fade = clamp(max(${scale}, 1e-5) / max(matPixelSize(), 1e-5), 0.0, 1.0);
  vec3 c = mix(${meanExpr}, rampVal(t, ${a.colorRamp}), fade);`
				: "vec3 c = vec3(t);";
			return `
  ${shiftStmt}float v = fbmVal(p, ${scale}, int(${a.octaves}), ${a.lacunarity}, ${a.persistence}, ${a.mode});
  float t = v;
  ${remap}${tShift}
  ${colorPart}
  return vec4(c, v * ${a.heightScale});`;
		},
		// Set11 Phase 1 — analytic derivative.  The VALUE channel must match fbmVal bit-for-bit (same function, same operation order — a mismatch would make the colour and the relief disagree).  fbmValD returns (value, dv/dx, dv/dz) per metre; the layer's height is v * heightScale, so the gradient is nd.yz * heightScale.
		derivBody: (a)=>{
			return `
  vec3 nd = fbmValD(p, ${scaledLen(a.scale, a.scaleSpace)}, int(${a.octaves}), ${a.lacunarity}, ${a.persistence}, ${a.mode});
  vec2 dh = nd.yz * ${a.heightScale};
  float h = nd.x * ${a.heightScale};
  return vec3(dh, h);`;
		},
		// Set11 Phase 2 — the sub-pixel slope variance: for each octave whose cellMeters < 2px (the band the gradient cannot represent — the exact double-counting rule), the mean-square slope (amp * heightScale / cellMeters)^2 accumulates.  The loop mirrors fbmVal's (same constant bound + break) so the D/L membership matches the value path.
		varianceBody: (a)=>{
			return `
  float px = matPixelSize();
  float vCell = max(${scaledLen(a.scale, a.scaleSpace)}, 1e-5);
  float vAmp = 1.0;
  float vTot = 0.0;
  for (int o = 0; o < 8; o++) {
    if (o >= int(${a.octaves})) break;
    if (vCell < 2.0 * px) {
      float ms = vAmp * ${a.heightScale} / vCell;
      vTot += ms * ms;
    }
    vAmp *= ${a.persistence};
    vCell /= max(${a.lacunarity}, 1.0001);
  }
  return vTot;`;
		},
	},
	scatter: {
		kind: "scatter", implemented: true, layerUsable: true, glsl: SCATTER_GLSL,
		argSchema: {
			// max raised 20 → 200 (2026-08-13): under `scaleSpace: 1` the density is a PIXEL
			// spacing, and a starfield wants ~20-30 px between stars.  min/max are validation-only
			// (RecipeSchema.test.ts) and never clamped in GLSL, so raising the ceiling is inert
			// for every existing recipe.
			density: {type: "scalar", min: 0.05, max: 200, default: 0.6},
			// 2 = cross / diffraction spike (2026-08-13; see Scatter.glsl.ts).
			shape: {type: "int", min: 0, max: 2, default: 0},
			aspect: {type: "scalar", min: 0.05, max: 20, default: 1},
			// Set3 §4.4 — rotation only; placement jitter is placeJitter's job (a full-rotation
			// chip layer must not also get full-swing placement, which snaps the lattice back).
			rotJitter: {type: "scalar", min: 0, max: 1, default: 1},
			placeJitter: {type: "scalar", min: 0, max: 1, default: 0.7},
			// Set3 §4.1 — the lattice-row breaker (fraction of cells that spawn a feature).
			presence: {type: "scalar", min: 0.05, max: 1, default: 1},
			// Set3 §3.1 (REVISED 2026-08-08) — per-cell radius as a fraction of the cell.
			// The defaults cap the max at 0.5 cell so adjacent pebbles in a populated row can
			// NEVER touch (diameter 2*radiusMax <= 1 cell) — the row-BAND fix: with radiusMax
			// > 0.5 a fully-populated row's discs overlap into a continuous horizontal band,
			// which is exactly the "rows of pebbles, no left-right variation" the dirt recipe
			// showed at 0.35..0.9.  A discrete scatter keeps BOTH directions varying.
			radiusMin: {type: "scalar", min: 0.05, max: 1, default: 0.18},
			radiusMax: {type: "scalar", min: 0.05, max: 1, default: 0.45},
			colorRamp: {type: "ramp", default: []},
			height: {type: "scalar", min: 0, max: 10, default: 0.05},
			colorJitterSeed: {type: "int", default: 0},
			// 2026-08-13 — PIXEL-LOCK: 0 = `density` is a spacing in METRES (default; byte-identical
			// GLSL), 1 = in PIXELS.  Pre-multiplying the density is enough: scatterSample derives
			// the cell (and therefore s.fade) from it, so the AA band-limit stays correct.
			scaleSpace: {type: "int", min: 0, max: 1, default: 0},
			// 2026-08-13 — COLOUR GATING.  scatter's colour is normally returned for the WHOLE
			// CELL (only s.fade attenuates it), which is fine for the heightBlend users (the dome
			// HEIGHT is their colour weight) but paints solid tiles for an EMISSIVE (add-blended,
			// height-less) layer.  0 = off (default; byte-identical GLSL), 1 = multiply the colour
			// by s.mask (a crisp core), 2 = multiply by s.dome (a soft halo).  1 and 2 also force
			// the returned height to 0, since a gated-colour layer is `contributes: "color"`.
			colorGate: {type: "int", min: 0, max: 2, default: 0},
		},
		layerBody: (a, specified, raw)=>{
			// Set3 §4.2 — the sub-pixel fade converges to the ramp's MEAN colour (a t=0.5 sample
			// of a non-linear ramp is NOT the mean; the zoomed-out wash fix for every scatter
			// user — dirt/mulch/sand/snow).  Literal ramp → JS-baked mean literal; ParamRef ramp
			// → in-shader rampMeanOfStops (slider-tracking + D10 free-variant safe).
			const meanExpr = specified.has("colorRamp")
				? meanFadeExpr(raw!.colorRamp, a.colorRamp)
				: "vec3(1.0)";
			const density = scaledLen(a.density, a.scaleSpace);
			// colorGate (see the argSchema note): 0 = off / byte-identical; 1 = crisp core (s.mask);
			// 2 = soft halo (s.dome).  Gated layers emit height 0 — they are emissive colour layers.
			const gate = parseInt(a.colorGate, 10);
			const gateMul = gate == 1 ? " * s.mask" : (gate == 2 ? " * s.dome" : "");
			const heightOut = gate == 0 ? "s.height" : "0.0";
			return `
  ScatterSample s = scatterSample(p, ${density}, ${a.shape}, ${a.aspect}, ${a.rotJitter}, ${a.placeJitter}, ${a.presence}, ${a.radiusMin}, ${a.radiusMax}, ${a.height}, ${a.colorJitterSeed});
  vec3 c = ${specified.has("colorRamp")
		? `mix(${meanExpr}, rampVal(hash01(uvec2(uint(s.cellID))), ${a.colorRamp}), s.fade)`
		: "vec3(1.0)"}${gateMul};
  return vec4(c, ${heightOut});`;
		},
	},
	worley: {
		kind: "worley", implemented: true, layerUsable: true, glsl: WORLEY_GLSL,
		argSchema: {
			scale: {type: "scalar", min: 0.05, max: 50, default: 0.8},
			jitter: {type: "scalar", min: 0, max: 1, default: 0.45},
			stoneRamp: {type: "ramp", default: []},
			mortarColor: {type: "color", default: [0.15, 0.14, 0.13]},
			grooveInner: {type: "scalar", min: 0, max: 1, default: 0.12},
			grooveOuter: {type: "scalar", min: 0, max: 1, default: 0.45},
			stoneHeight: {type: "scalar", min: 0, max: 1, default: 0.08},
			grooveDepth: {type: "scalar", min: -1, max: 0, default: -0.02},
		},
		layerBody: (a, specified, raw)=>{
			// Set3 §4.2 — the deep-zoom fade converges to the stone ramp's MEAN (a t=0.5 sample
			// of a non-linear ramp is NOT the mean — the cobble zoom-out wash fix).  Literal →
			// JS-baked mean; ParamRef → in-shader rampMeanOfStops.
			const stoneMean = specified.has("stoneRamp")
				? meanFadeExpr(raw!.stoneRamp, a.stoneRamp)
				: "vec3(1.0)";
			return `
  WorleySample w = worleySample(p / ${a.scale}, ${a.jitter});
  // AA (plan §7): groove edges widened by the pixel footprint (px in cell units) + per-scale
  // band-limit fade — when the stone cell falls below ~2px the whole pattern fades toward the
  // stone-colour mean (mortar grooves become sub-pixel and must average away rather than sizzle).
  float pxC = max(matPixelSize() / max(${a.scale}, 1e-5), 1e-5);
  // AA (plan §7): the groove transition is already a smoothstep (never step()); scaleFade
  // handles the deep-zoom fade.  pxC = cells per pixel; a stone cell spans 1/pxC px.
  float groove = 1.0 - smoothstep(${a.grooveInner}, ${a.grooveOuter}, w.edgeDist);
  // Fade = 1 for cells > ~2px, 0 below ~1px (spec-clean edges — the plan §7 form inverts
  // them, which GLSL ES 300 leaves undefined).  scaleFade = 1 → the per-stone pattern
  // (stone/mortar mix); scaleFade = 0 → the stone-colour mean (sub-pixel average).
  float scaleFade = 1.0 - smoothstep(0.5, 1.0, pxC);
  vec3 stoneC = rampVal(hash01(uvec2(uint(w.cellID))), ${a.stoneRamp});
  vec3 mortarC = ${a.mortarColor};
  vec3 c = mix(${stoneMean}, mix(stoneC, mortarC, groove), scaleFade);
  float h = mix(${a.stoneHeight}, mix(${a.stoneHeight}, ${a.grooveDepth}, groove), scaleFade);
  return vec4(c, h);`;
		},
	},
	// ── Phase 8 primitives ────────────────────────────────────────────────────────────────
	// `waves` carries the WAVES_GLSL prelude (wavesVal + domainWarpP); `domainWarp` is a
	// p-TRANSFORMER, not a layer — it is composed INSIDE the pattern primitives that need it
	// (waves/domainRepeat take warpScale/warpAmount args), so it is implemented-but-not-
	// layer-usable and contributes no prelude of its own.  Same for radialDistToWarpedAxis,
	// whose GLSL ships with DOMAIN_REPEAT_GLSL (its only consumer is the wood pattern).
	waves: {
		kind: "waves", implemented: true, layerUsable: true, glsl: WAVES_GLSL,
		argSchema: {
			wavelength: {type: "scalar", min: 0.001, max: 100, default: 0.3},
			angle: {type: "scalar", min: 0, max: 360, default: 0},
			sharpness: {type: "scalar", min: 0.05, max: 8, default: 1},
			// Set3 §3.4 — the second wave train (degrees + strength 0..1, default 0 = disabled):
			// two crossing trains keep crests from ever staying parallel (real ripple morphology).
			crossAngle: {type: "scalar", min: 0, max: 360, default: 0},
			crossStrength: {type: "scalar", min: 0, max: 1, default: 0},
			// The cross train's wavelength as a MULTIPLE of the main one. Must be incommensurate
			// (default 1.7); at 1.0 the equal-wavelength trains beat into a rigid moire lattice.
			crossWavelengthRatio: {type: "scalar", min: 0.1, max: 8, default: 1.7},
			// Set3 §3.4.3 — the asymmetric crest profile (1 = symmetric; >1 = steeper lee side).
			asymm: {type: "scalar", min: 1, max: 8, default: 1},
			warpScale: {type: "scalar", min: 0.01, max: 100, default: 1},
			warpOctaves: {type: "int", min: 1, max: 8, default: 3},
			warpAmount: {type: "scalar", min: 0, max: 20, default: 0},
			colorRamp: {type: "ramp", default: []},
			heightScale: {type: "scalar", min: 0, max: 10, default: 0},
		},
		layerBody: (a, specified)=>`
  vec2 pw = domainWarpP(p, ${a.warpScale}, ${a.warpOctaves}, ${a.warpAmount});
  float v = wavesVal(pw, ${a.wavelength}, ${a.angle}, ${a.sharpness}, ${a.crossAngle}, ${a.crossStrength}, ${a.crossWavelengthRatio}, ${a.asymm});
  ${specified.has("colorRamp") ? `vec3 c = rampVal(v, ${a.colorRamp});` : "vec3 c = vec3(v);"}
  // Height is CENTRED (v - 0.5): a wave train must not add a DC offset to the height chain,
  // else the layers above it (heightBlend colour weights) see a constant lift.
  return vec4(c, (v - 0.5) * ${a.heightScale});`,
		// Set11 Phase 1 — analytic derivative.  The VALUE channel reuses the layer wrapper (the height (v - 0.5) * heightScale) and the gradient comes from wavesValD (the warp treated as locally constant — the Stage-1 staging decision; the call must use the WARPED p (pw) exactly like the layer body).
		derivBody: (a, _specified, _raw, layerFnNameForDeriv)=>{
			return `
  vec2 pw = domainWarpP(p, ${a.warpScale}, ${a.warpOctaves}, ${a.warpAmount});
  float h = ${layerFnNameForDeriv}(p).a;
  // The layer's height is (v - 0.5) * heightScale where v = mix(0.5, rawV, fade) — the pixel fade (smoothstep(1px, 2px, wl)) is a p-independent multiplier of (v - 0.5), so the TRUE gradient is fade * dvRaw * heightScale.  wavesValD returns the RAW gradient; applying the fade keeps gradient and value consistent at every zoom (at coarse zoom the faded value is a constant, so its gradient must collapse to ~0 — skipping the fade would invent sub-pixel relief, exactly the aliasing Phase 1 removes).
  float fade = smoothstep(1.0 * matPixelSize(), 2.0 * matPixelSize(), max(${a.wavelength}, 1e-5));
  vec2 dh = wavesValD(pw, ${a.wavelength}, ${a.angle}, ${a.sharpness}, ${a.crossAngle}, ${a.crossStrength}, ${a.crossWavelengthRatio}, ${a.asymm}) * fade * ${a.heightScale};
  return vec3(dh, h);`;
		},
		// Set11 Phase 2 — sub-pixel slope variance.  A single wave train is one sinusoid: the sub-pixel contribution is the mean-square slope of the band-faded part — (1 - fade) * 0.5 * (A * 2pi/wl)^2 with A = heightScale/2 (the pixel fade is the sub-pixel FRACTION; the mean-square slope of a sinusoid of amplitude A and wavelength wl is 0.5 * (A * 2pi/wl)^2).
		varianceBody: (a)=>{
			return `
  float wl = max(${a.wavelength}, 1e-5);
  float fade = smoothstep(1.0 * matPixelSize(), 2.0 * matPixelSize(), wl);
  float subPx = 1.0 - clamp(fade, 0.0, 1.0);
  float ampM = ${a.heightScale} * 0.5;
  float ms = (ampM * 6.28318530718 / wl);
  return subPx * 0.5 * ms * ms;`;
		},
	},
	domainWarp: {
		kind: "domainWarp", implemented: true, layerUsable: false, argSchema: {}, glsl: "",
		layerBody: missingBody("domainWarp"),
	},
	// Set3 §4.5 — iso-contour bands of a turbulence field (Perlin-marble veins / snow drifts).
	// bandedNoiseVal returns [0,1] with a DC bias (a sin remap), so the colour ramp is applied
	// directly and the height is CENTRED (band - 0.5) like waves.
	bandedNoise: {
		kind: "bandedNoise", implemented: true, layerUsable: true, glsl: BANDED_NOISE_GLSL,
		argSchema: {
			scale: {type: "scalar", min: 0.02, max: 50, default: 1},
			veining: {type: "scalar", min: 0.2, max: 12, default: 2},
			sharpness: {type: "scalar", min: 0.05, max: 8, default: 1},
			colorRamp: {type: "ramp", default: []},
			heightScale: {type: "scalar", min: 0, max: 10, default: 0},
		},
		layerBody: (a, specified, raw)=>{
			// Set3 §4.2/§4.5 — the mean-converging AA fade: the sin remap is DC-biased, so the
			// sub-pixel fade target must be the ramp mean over the WARPED band domain (the
			// sharpness-remapped t — a sharp=1 sinusoid has mean 2/pi ~ 0.637).  Literal ramp →
			// JS-baked mean (Simpson-quadrature over t^sharpness); ParamRef ramp → the in-shader
			// piecewise-linear mean of the stops (uniform values, no JS staleness).  The fade
			// gate is the band spacing (scale/veining) vs the pixel — sub-pixel bands average
			// toward the mean instead of sizzling.
			const meanExpr = specified.has("colorRamp")
				? meanFadeExpr(raw!.colorRamp, a.colorRamp, parseFloat(a.sharpness))
				: "vec3(band)";
			const bandSpacing = `max(${a.scale} / max(${a.veining}, 1e-5), 1e-5)`;
			return `
  float band = bandedNoiseVal(p, ${a.scale}, ${a.veining}, ${a.sharpness});
  vec3 c = ${specified.has("colorRamp")
		? `mix(${meanExpr}, rampVal(band, ${a.colorRamp}), smoothstep(1.0 * matPixelSize(), 2.0 * matPixelSize(), ${bandSpacing}))`
		: meanExpr};
  return vec4(c, (band - 0.5) * ${a.heightScale});`;
		},
	},
	// Set11 Phase 3 — the coverage field: a continuous, thresholdable field with connected, ragged, dilatable regions and sub-pixel-correct edges (fixes the foam confetti — scatter is a stamp lattice, structurally incapable of streaky connected patches).  Follow the worley pattern: emit colour + height directly (the coverage must NOT live in .a — that would collide with the height convention).
	coverageField: {
		kind: "coverageField", implemented: true, layerUsable: true, glsl: COVERAGE_FIELD_GLSL,
		argSchema: {
			scale: {type: "scalar", min: 0.02, max: 200, default: 1},
			octaves: {type: "scalar", min: 1, max: 8, default: 4},
			mode: {type: "int", min: 0, max: 3, default: 3},
			anisotropy: {type: "scalar", min: 1, max: 50, default: 1},
			angle: {type: "scalar", min: 0, max: 360, default: 0},
			threshold: {type: "scalar", min: 0, max: 1, default: 0.5},
			edgeSoftness: {type: "scalar", min: 0, max: 0.5, default: 0.08},
			warpScale: {type: "scalar", min: 0.05, max: 100, default: 4},
			warpOctaves: {type: "int", min: 1, max: 8, default: 3},
			warpAmount: {type: "scalar", min: 0, max: 20, default: 0},
			colorRamp: {type: "ramp", default: []},
			heightScale: {type: "scalar", min: 0, max: 10, default: 0},
		},
		layerBody: (a, specified, raw)=>{
			// Set3 §4.2 — the mean-converging AA fade: below ~1 px per feature the whole field
			// fades toward the ramp's MEAN (a t=0.5 sample is NOT the mean).  Literal ramp →
			// JS-baked mean; ParamRef ramp → in-shader rampMeanOfStops.
			const meanExpr = specified.has("colorRamp")
				? meanFadeExpr(raw!.colorRamp, a.colorRamp)
				: "vec3(0.5)";
			const colorPart = specified.has("colorRamp")
				? `float fade = smoothstep(1.0 * matPixelSize(), 2.0 * matPixelSize(), max(${a.scale}, 1e-5));
  vec3 c = mix(${meanExpr}, rampVal(cov, ${a.colorRamp}), fade);`
				: "vec3 c = vec3(cov);";
			return `
  float cov = coverageFieldVal(p, ${a.scale}, int(${a.octaves}), ${a.mode}, ${a.anisotropy}, ${a.angle}, ${a.threshold}, ${a.edgeSoftness}, ${a.warpScale}, ${a.warpAmount});
  ${colorPart}
  return vec4(c, cov * ${a.heightScale});`;
		},
	},
	// The wood/plank pattern: board lattice + per-board reseed + warped-axis growth rings +
	// anisotropic grain + rare knots + the inter-board gap (colour + height).
	domainRepeat: {
		kind: "domainRepeat", implemented: true, layerUsable: true, glsl: DOMAIN_REPEAT_GLSL,
		argSchema: {
			period: {type: "scalar", min: 0.02, max: 5, default: 0.14},
			lengthPeriod: {type: "scalar", min: 0.1, max: 50, default: 1.8},
			stagger: {type: "scalar", min: 0, max: 1, default: 1},
			ringWavelength: {type: "scalar", min: 0.001, max: 1, default: 0.012},
			ringSharpness: {type: "scalar", min: 0.05, max: 8, default: 1.6},
			grainScale: {type: "scalar", min: 0.001, max: 2, default: 0.02},
			grainAspect: {type: "scalar", min: 1, max: 50, default: 20},
			warpScale: {type: "scalar", min: 0.01, max: 20, default: 0.6},
			warpAmount: {type: "scalar", min: 0, max: 2, default: 0.06},
			woodRamp: {type: "ramp", default: []},
			gapColor: {type: "color", default: [0.06, 0.05, 0.04]},
			gapWidth: {type: "scalar", min: 0, max: 0.05, default: 0.004},
			plankHeight: {type: "scalar", min: 0, max: 0.2, default: 0.004},
			gapDepth: {type: "scalar", min: -0.2, max: 0, default: -0.006},
			knotChance: {type: "scalar", min: 0, max: 1, default: 0.06},
			knotRadius: {type: "scalar", min: 0.001, max: 0.2, default: 0.02},
		},
		layerBody: (a, specified, raw)=>{
			// Set3 §3.7.1 — the gap's sub-pixel fade target is the wood ramp's MEAN (the board
			// brown the surface should converge to at coarse zoom).  Literal ramp → JS-baked
			// mean; ParamRef ramp → in-shader rampMeanOfStops (slider-tracking + D10-safe).
			const woodMean = specified.has("woodRamp")
				? meanFadeExpr(raw!.woodRamp, a.woodRamp)
				: "vec3(0.5)";
			return `
  RepeatSample rs = domainRepeatAxis(p, ${a.period}, ${a.lengthPeriod}, ${a.stagger});
  // Per-board reseed (plan §4.2: "per-plank cellID reseed"): shift the board-local coords by a
  // per-board offset so no two boards share a ring/grain pattern.  Kept SMALL (tens of metres,
  // not thousands) — sin()'s argument in wavesVal is dist/ringWavelength, and a huge offset
  // divided by a ~1 cm wavelength loses float mantissa precision (banding).
  vec2 seedOff = vec2(hash01(uvec2(uint(rs.cellID), 11u)), hash01(uvec2(uint(rs.cellID), 29u))) * 37.0;
  vec2 pl = rs.pLocal + seedOff;
  // Growth rings: distance from the board's warped centre line, banded.  Fed to wavesVal along
  // +x as a 1D signal (the distance IS the phase).  Cross-train/asymm defaults (0/0/1) keep
  // the rings symmetric single-train.
  float rd = radialDistToWarpedAxis(pl, vec2(1.0, 0.0), ${a.warpScale}, ${a.warpAmount});
  float rings = wavesVal(vec2(rd, 0.0), ${a.ringWavelength}, 0.0, ${a.ringSharpness}, 0.0, 0.0, 1.0, 1.0);
  // Anisotropic grain (~20:1 along the board): dividing x by the aspect stretches features along x.
  float grain = fbmVal(vec2(pl.x / max(${a.grainAspect}, 1.0), pl.y), ${a.grainScale}, 4, 2.0, 0.5, 0);
  float t = clamp(rings * 0.8 + (grain * 0.5 + 0.5) * 0.2, 0.0, 1.0);
  // Rare knots: one candidate per board, gated by a per-board hash.
  float knot = 0.0;
  if (hash01(uvec2(uint(rs.cellID), 5u)) < ${a.knotChance}) {
    vec2 kc = vec2(hash01(uvec2(uint(rs.cellID), 17u)) * ${a.lengthPeriod},
                   (hash01(uvec2(uint(rs.cellID), 23u)) - 0.5) * ${a.period});
    float kd = length(rs.pLocal - kc);
    knot = 1.0 - smoothstep(max(${a.knotRadius}, 1e-5) * 0.5, max(${a.knotRadius}, 1e-5), kd);
  }
  t = clamp(t - knot * 0.6, 0.0, 1.0);
  ${specified.has("woodRamp") ? `vec3 c = rampVal(t, ${a.woodRamp});` : "vec3 c = vec3(t);"}
  // Board gap — Set3 §3.7 (the wood zoom-out black fix).  The gap colour/height must PIXEL-FADE
  // toward the board values as the gap shrinks below ~2px: at coarse zoom a sub-pixel gap whose
  // near-black colour and negative relief stayed at full strength swallowed the whole board
  // (at mpp >= board width the entire surface was gap-mixed → near-black "wood").
  //   1) Sub-pixel gap fade: the gap colour blends toward the BOARD-MEAN colour as the gap
  //      shrinks below ~2px — the "shrink in an image editor" result (below ~2px per board the
  //      whole surface converges to board brown).
  //   2) The edge band is bounded to the GAP WIDTH, not the pixel size: soft = clamp(px, 1e-4,
  //      gapW) so a zoomed-in gap stays a crisp hairline but never swallows the board.
  //   3) The gap HEIGHT is pixel-faded too: the negative relief collapses to ~0 at coarse zoom
  //      (removes the dark shading the sub-pixel gap edge otherwise adds to the detail normal).
  float gapW = max(${a.gapWidth}, 1e-5);
  float gapFade = smoothstep(1.0 * matPixelSize(), 2.0 * matPixelSize(), gapW);
  float soft = clamp(matPixelSize(), 1e-4, gapW);
  float gap = 1.0 - smoothstep(max(gapW - soft, 0.0), gapW + soft, rs.edge);
  c = mix(c, mix(${a.gapColor}, ${woodMean}, gapFade), gap);
  float gapHeight = mix(${a.gapDepth}, 0.0, gapFade);
  return vec4(c, mix(${a.plankHeight}, gapHeight, gap));`;
		},
	},
	// Coverage mask (snow/dirt).  NOTE the channel convention: a mask layer emits its MASK in
	// the .a channel (not a height) and its "uncovered" fallback colour in .rgb, which is what
	// the `maskedBy` blend consumes.  Such a layer must therefore be declared
	// `contributes: "color"` — as "both" the mask value would leak into the height chain.
	mask: {
		kind: "mask", implemented: true, layerUsable: true, glsl: MASK_GLSL,
		argSchema: {
			slopeStart: {type: "scalar", min: 0, max: 1, default: 0.25},
			slopeEnd: {type: "scalar", min: 0, max: 1, default: 0.6},
			uncoveredColor: {type: "color", default: [0.35, 0.32, 0.29]},
		},
		layerBody: a=>`
  float m = maskVal(${a.slopeStart}, ${a.slopeEnd});
  return vec4(${a.uncoveredColor}, m);`,
	},
	// Elevation-reactive colour (2026-08-13, water depth): maps the per-fragment TERRAIN HEIGHT
	// (matGeoHeight() — the u_geoHeight lattice, the worker's heights) through a ramp.  This is
	// the LARGE-SCALE colour water is missing (its existing depth layer is 14 m and fades to its
	// mean at map-overview): an elevation ramp is genuinely km-scale AND physically motivated
	// (deep navy over deep seabed, teal over shallow shelf).  Unlike `fbm`, the input is NOT
	// procedural noise — it is the actual terrain height, so it is world-fixed and never aliases
	// (the height lattice is shared on chunk edges → seam-free; the round() mapping is exact).
	// `minHeight`/`maxHeight` are metres (map the elevation range onto the ramp's [0,1] t — the
	// layer body clamps, so out-of-range elevations use the nearest ramp end).  Colour ONLY
	// (heightScale 0) — the water recipe blends it with `over` + `blendFactor` as its base.
	// Ramp is REQUIRED (unlike fbm, a bare greyscale elevation is rarely useful).
	geoHeight: {
		kind: "geoHeight", implemented: true, layerUsable: true, glsl: "",
		argSchema: {
			minHeight: {type: "scalar", min: -5000, max: 5000, default: -100},
			maxHeight: {type: "scalar", min: -5000, max: 5000, default: 100},
			colorRamp: {type: "ramp", default: []},
			// heightScale is normally 0 (colour-only).  Kept for consistency + a hypothetical
			// relief pass, but elevation isn't a height field you usually want to double as relief.
			heightScale: {type: "scalar", min: 0, max: 10, default: 0},
		},
		layerBody: (a, specified)=>{
			if (!specified.has("colorRamp")) throw new Error("[PrimitiveRegistry] geoHeight layer must declare a colorRamp (elevation → colour)");
			const t = `clamp((matGeoHeight() - ${a.minHeight}) / max(${a.maxHeight} - ${a.minHeight}, 1e-5), 0.0, 1.0)`;
			return `
  float t = ${t};
  vec3 c = rampVal(t, ${a.colorRamp});
  return vec4(c, matGeoHeight() * ${a.heightScale});`;
		},
	},
	radialDistToWarpedAxis: {
		kind: "radialDistToWarpedAxis", implemented: true, layerUsable: false, argSchema: {}, glsl: "",
		layerBody: missingBody("radialDistToWarpedAxis"),
	},
	// ── Wood (2026-08-24) — the seam-free CONTINUOUS wood surface ────────────────────────────────
	// The planks direction (board lattice + inter-board gap) lives on its own `woodPlanks` recipe
	// + `domainRepeat` primitive.  This `woodSurface` primitive keeps ONLY the plank texture that
	// reads as "unmistakably wood" while being continuous — no seams/plank-lines anywhere:
	// the growth rings (warped-axis distance through a sinusoid band) + the anisotropic grain +
	// rare whole-surface knots, evaluated in WORLD space (no board reseed, no per-board lattice).
	// Like the building-surface primitives it composes ONLY the existing prelude helpers
	// (wavesVal/radialDistToWarpedAxis/fbmVal/hash01/rampVal), so it is a prelude-layer-body
	// primitive (glsl:"" + implemented): the terrain path and every pre-existing built-in recipe
	// emit byte-identical GLSL (the P6 regression gate), and it needs no new GLSL to share.
	woodSurface: {
		kind: "woodSurface", implemented: true, layerUsable: true, glsl: "",
		argSchema: {
			grainScale: {type: "scalar", min: 0.001, max: 2, default: 0.02},
			grainAspect: {type: "scalar", min: 1, max: 50, default: 20},
			ringWavelength: {type: "scalar", min: 0.001, max: 1, default: 0.012},
			ringSharpness: {type: "scalar", min: 0.05, max: 8, default: 1.6},
			ringSpacing: {type: "scalar", min: 0.002, max: 1, default: 0.6},
			warpScale: {type: "scalar", min: 0.01, max: 20, default: 0.6},
			warpAmount: {type: "scalar", min: 0, max: 2, default: 0.06},
			woodRamp: {type: "ramp", default: []},
			knotChance: {type: "scalar", min: 0, max: 1, default: 0.06},
			knotRadius: {type: "scalar", min: 0.001, max: 0.2, default: 0.02},
			heightScale: {type: "scalar", min: 0, max: 0.2, default: 0},
		},
		layerBody: (a, specified, raw)=>{
			// Set3 §4.2 — the sub-pixel fade target is the wood ramp's MEAN (the board brown the
			// surface should converge to at coarse zoom).  Literal ramp → JS-baked mean; ParamRef
			// ramp → in-shader rampMeanOfStops (slider-tracking + D10-safe).
			const woodMean = specified.has("woodRamp")
				? meanFadeExpr(raw!.woodRamp, a.woodRamp)
				: "vec3(0.5)";
			// t = 0 is the ring core/knot (darkest), t = 1 the pale latewood between rings.
			return `
  // Growth rings: distance from the warped centre line (through the origin, axis +x), banded.
  // First fed to radialDistToWarpedAxis (the z-comp of the warped axis dist) for the LOCAL
  // "cathedral-ring" banding, then the ring intersection RATE (the y-comp — how fast the
  // sawn face crosses the growth cone) sets the RING SPACING via ld = dist / rate.
  float aWarp = ${a.warpAmount};
  vec2 wDir = vec2(1.0, 0.0);
  vec2 wnrm = vec2(0.0, 1.0);
  float along = dot(p, wDir);
  float across = dot(p, wnrm);
  float warp = (valueNoise(vec2(along / max(${a.warpScale}, 1e-4), 0.37)) - 0.5) * 2.0 * aWarp;
  float dist = abs(across + warp);
  // Radial gradient of the warped axis (rate at which the face crosses the cone) — a constant
  // here, but kept symbolic so the spacing sweeps naturally without any plank/board seams.
  float rate = 1.0;
  float ld = dist / max(rate, 1e-4);
  float rings = wavesVal(vec2(ld, 0.0), ${a.ringWavelength}, 0.0, ${a.ringSharpness}, 0.0, 0.0, 1.0, 1.0);
  // Anisotropic grain (~20:1 along the axis): dividing x by the aspect stretches features along x.
  float grain = fbmVal(vec2(p.x / max(${a.grainAspect}, 1.0), p.y), ${a.grainScale}, 4, 2.0, 0.5, 0);
  float t = clamp(rings * 0.8 + (grain * 0.5 + 0.5) * 0.2, 0.0, 1.0);
  // Rare knots: one candidate per world-space tile of size ringSpacing x ringSpacing, so a knot
  // is a localised feature rather than a per-board property (there are no boards here).
  float knot = 0.0;
  float tile = max(${a.ringSpacing}, 1e-4);
  ivec2 cell = ivec2(floor(p / tile));
  // Combine the tile coords into a single scalar hash base, then salt each use with a distinct
  // uvec2(uint, uint) (GLSL-safe: 2 components for a 2-component vector — no mixed-arity ctor).
  int cellHash = cell.x * 2654435761 + cell.y * 40503;
  if (hash01(uvec2(uint(cellHash), 5u)) < ${a.knotChance}) {
    vec2 kc = vec2(hash01(uvec2(uint(cellHash), 17u)),
                   (hash01(uvec2(uint(cellHash), 23u)) - 0.5)) * tile;
    float kd = length(p - (vec2(cell) + 0.5) * tile - kc);
    knot = 1.0 - smoothstep(max(${a.knotRadius}, 1e-5) * 0.5, max(${a.knotRadius}, 1e-5), kd);
  }
  t = clamp(t - knot * 0.6, 0.0, 1.0);
  ${specified.has("woodRamp") ? `vec3 c = rampVal(t, ${a.woodRamp});` : "vec3 c = vec3(t);"}
  // A hair of ring relief so the surface reads as real wood under the specular lobe (crown sits
  // ON the rings; the gap relief is gone — there are no plank gaps in this recipe).
  float h = rings * ${a.heightScale};
  // Sub-pixel converge toward the wood mean so zoom-out stays wood-brown (no sizzle/banding).
  float fade = smoothstep(0.5 * matPixelSize(), 1.5 * matPixelSize(), ${a.ringWavelength});
  c = mix(c, ${woodMean}, 1.0 - fade);
  return vec4(c, h);`;
		},
	},
	// ── Buildings Set1 §7.4 — building-surface primitives ────────────────────────────────────────
	// All compose the EXISTING prelude helpers (domainRepeatAxis/hash01/fbmVal/rampVal), so their
	// GLSL is an empty share (they are prelude-layer-body primitives — the prelude already carries
	// every helper they call). This keeps them strictly ADDITIVE: the terrain path and every
	// pre-existing built-in recipe emit byte-identical GLSL (the P6 regression gate). No
	// analytic-derivative bodies are provided (the synthesised per-layer finite difference is used,
	// correct + band-limited — fine for building surfaces at typical view distances).

	// Brick bonding: running-bond courses with a mortar groove. `brickLength` × `brickHeight`
	// courses; each course is offset by half a brick (`stagger 0..1`, default 0.5 = running bond)
	// via domainRepeatAxis's per-row stagger. Brick colour from `brickRamp` (a per-brick jitter),
	// mortar from `mortarColor`; `mortarWidth` controls the groove; relief from `mortarDepth`
	// (negative = the mortar recedes). `courseHeight` gives the geometry-normal-preserving relief
	// on the brick face itself.
	brickBond: {
		kind: "brickBond", implemented: true, layerUsable: true, glsl: "", argSchema: {
			brickLength: {type: "scalar", min: 0.08, max: 1, default: 0.23},
			brickHeight: {type: "scalar", min: 0.02, max: 0.3, default: 0.066},
			stagger: {type: "scalar", min: 0, max: 1, default: 0.5},
			mortarWidth: {type: "scalar", min: 0, max: 0.02, default: 0.006},
			mortarDepth: {type: "scalar", min: -0.02, max: 0, default: -0.003},
			brickRelief: {type: "scalar", min: 0, max: 0.05, default: 0.002},
			brickRamp: {type: "ramp", default: []},
			mortarColor: {type: "color", default: [0.72, 0.7, 0.66]},
		},
		layerBody: (a, specified, raw)=>{
			const meanE = specified.has("brickRamp") ? meanFadeExpr(raw!.brickRamp, a.brickRamp) : "vec3(0.6)";
			return `
  // Board-lattice pitch: period = brick HEIGHT (across the course), lengthPeriod = brick LENGTH
  // (along the course), so the diagram reads as walls do (a course running horizontally).
  RepeatSample rs = domainRepeatAxis(p, ${a.brickHeight}, ${a.brickLength}, ${a.stagger});
  // Running bond: the per-course stagger from domainRepeatAxis's row offset.
  // Per-brick jitter on the brick colour (stable via the cell hash): keeps each brick slightly
  // distinct within the ramp range without scattering the whole face.
  float jitter = hash01(uvec2(uint(rs.cellID), 3u));
  vec3 brickC = ${specified.has("brickRamp") ? `rampVal(jitter, ${a.brickRamp})` : "vec3(0.6)"};
  // Mortar groove: a band along the brick perimeter (edge = distance to nearest board boundary).
  float soft = clamp(matPixelSize(), 1e-5, ${a.mortarWidth});
  float groove = 1.0 - smoothstep(max(${a.mortarWidth} - soft, 0.0), ${a.mortarWidth} + soft, rs.edge);
  // Sub-pixel gap fade toward the brick mean so a zoomed-out wall doesn't fill with mortar.
  float fade = smoothstep(1.0 * matPixelSize(), 2.0 * matPixelSize(), max(${a.mortarWidth}, 1e-5));
  vec3 c = mix(brickC, ${meanE}, groove * (1.0 - fade));
  vec3 mortarC = ${a.mortarColor};
  c = mix(c, mortarC, groove * fade);
  float h = ${a.brickRelief} - groove * max(-${a.mortarDepth}, 0.0);
  return vec4(c, h);`;
		},
	},
	// Horizontal siding/lap boards: horizontal laps each casting a thin shadow line (relief) under
	// the lap, plus a board-height palette. Doubles as a wood-floor plank with a different axis.
	siding: {
		kind: "siding", implemented: true, layerUsable: true, glsl: "", argSchema: {
			boardHeight: {type: "scalar", min: 0.05, max: 1, default: 0.15},
			lapOverlap: {type: "scalar", min: 0, max: 0.3, default: 0.02},
			shadowDepth: {type: "scalar", min: 0, max: 0.02, default: 0.004},
			boardRamp: {type: "ramp", default: []},
			gapColor: {type: "color", default: [0.1, 0.09, 0.08]},
		},
		layerBody: (a, specified, raw)=>{
			const meanE = specified.has("boardRamp") ? meanFadeExpr(raw!.boardRamp, a.boardRamp) : "vec3(0.7)";
			return `
  // Horizontal boards along y = up: split the plane into bands of boardHeight.
  float yb = p.y / max(${a.boardHeight}, 1e-4);
  float band = floor(yb);
  float fy = yb - band; // 0..1 down the board
  float off = hash01(uvec2(uint(int(band) * 2654435761), 13u)) * 0.3;
  vec3 boardC = ${specified.has("boardRamp") ? `rampVal(hash01(uvec2(uint(int(band) * 2654435761), 17u)), ${a.boardRamp})` : "vec3(0.7)"};
  // Gap + shadow line at the top of each band (under the lap).
  float soft = clamp(matPixelSize(), 1e-5, max(${a.lapOverlap}, 1e-5));
  float gap = smoothstep(0.0, soft, fy); // 0 right at the lap => shadow
  float shade = (1.0 - gap) * ${a.shadowDepth};
  vec3 c = mix(${a.gapColor}, boardC, gap);
  float fade = smoothstep(1.0 * matPixelSize(), 2.0 * matPixelSize(), ${a.boardHeight});
  c = mix(c, ${meanE}, (1.0 - gap) * (1.0 - fade));
  return vec4(c, shade);`;
		},
	},
	// Roof shingles: offset scale/tab rows with per-tab colour jitter. Uses the same row lattice as
	// siding but with a per-tab offset (`stagger`) and a tab height << block height, giving the
	// classic staggered asphalt-shingle read (each tab tinted slightly differently).
	shingles: {
		kind: "shingles", implemented: true, layerUsable: true, glsl: "", argSchema: {
			tabWidth: {type: "scalar", min: 0.05, max: 1, default: 0.2},
			tabHeight: {type: "scalar", min: 0.02, max: 0.3, default: 0.05},
			stagger: {type: "scalar", min: 0, max: 1, default: 0.5},
			shingleRamp: {type: "ramp", default: []},
			shadowDepth: {type: "scalar", min: 0, max: 0.02, default: 0.004},
			gapColor: {type: "color", default: [0.08, 0.07, 0.06]},
		},
		layerBody: (a, specified, raw)=>{
			const meanE = specified.has("shingleRamp") ? meanFadeExpr(raw!.shingleRamp, a.shingleRamp) : "vec3(0.4)";
			return `
  RepeatSample rs = domainRepeatAxis(p, ${a.tabHeight}, ${a.tabWidth}, ${a.stagger});
  float fy = rs.pLocal.y / max(${a.tabWidth}, 1e-4); // across the tab (0..1)
  float fx = rs.pLocal.x / max(${a.tabHeight}, 1e-4); // along the tab
  vec3 tabC = ${specified.has("shingleRamp") ? `rampVal(hash01(uvec2(uint(rs.cellID), 7u)), ${a.shingleRamp})` : "vec3(0.4)"};
  // Tab edge = the domainRepeat edge (inter-board gap).
  float soft = clamp(matPixelSize(), 1e-5, max(${a.shadowDepth}, 1e-5));
  float edge = smoothstep(0.0, soft * 4.0, rs.edge);
  float shade = (1.0 - edge) * ${a.shadowDepth};
  vec3 c = mix(${a.gapColor}, tabC, edge);
  float fade = smoothstep(1.0 * matPixelSize(), 2.0 * matPixelSize(), max(${a.tabWidth}, 1e-5));
  c = mix(c, ${meanE}, (1.0 - edge) * (1.0 - fade));
  return vec4(c, shade);`;
		},
	},
	// Barrel (Spanish) roof tiles: ridging along one axis via waves over a coarse lattice. A row of
	// tile crests + valleys along the slope direction; the perpendicular axis repeats the row.
	barrelTiles: {
		kind: "barrelTiles", implemented: true, layerUsable: true, glsl: "", argSchema: {
			tileWidth: {type: "scalar", min: 0.05, max: 0.5, default: 0.15},
			tileLength: {type: "scalar", min: 0.05, max: 1, default: 0.3},
			crestHeight: {type: "scalar", min: 0, max: 0.05, default: 0.012},
			tileRamp: {type: "ramp", default: []},
			gapColor: {type: "color", default: [0.2, 0.16, 0.12]},
		},
		layerBody: (a, specified, raw)=>{
			const meanE = specified.has("tileRamp") ? meanFadeExpr(raw!.tileRamp, a.tileRamp) : "vec3(0.5)";
			return `
  // Tile lattice: period = tile length (along slope), lengthPeriod = tile width (across).
  RepeatSample rs = domainRepeatAxis(p, ${a.tileLength}, ${a.tileWidth}, 0.0);
  float crest = (wavesVal(rs.pLocal, ${a.tileLength} * 0.5, 0.0, 2.0, 0.0, 0.0, 1.0, 1.0) + 1.0) * 0.5;
  float h = crest * ${a.crestHeight};
  float soft = clamp(matPixelSize(), 1e-5, 0.02);
  float grooved = 1.0 - smoothstep(max(0.02 - soft, 0.0), 0.02 + soft, rs.edge);
  vec3 tileC = ${specified.has("tileRamp") ? `rampVal(hash01(uvec2(uint(rs.cellID), 19u)), ${a.tileRamp})` : "vec3(0.5)"};
  vec3 c = mix(${a.gapColor}, tileC, 1.0 - grooved);
  float fade = smoothstep(1.0 * matPixelSize(), 2.0 * matPixelSize(), max(${a.tileLength}, 1e-5));
  c = mix(c, ${meanE}, grooved * (1.0 - fade));
  return vec4(c, h);`;
		},
	},
	// Metal standing-seam roof: flat panels (typically wider than traditional tiles) separated by
	// periodic raised seams running down-slope. `seamSpacing` = panel width; `seamHeight` = the
	// raised ridge. `seamRamp` colours the panels (a subtle per-panel tint).
	standingSeam: {
		kind: "standingSeam", implemented: true, layerUsable: true, glsl: "", argSchema: {
			seamSpacing: {type: "scalar", min: 0.2, max: 3, default: 0.6},
			seamWidth: {type: "scalar", min: 0.002, max: 0.05, default: 0.01},
			seamHeight: {type: "scalar", min: 0, max: 0.05, default: 0.015},
			seamRamp: {type: "ramp", default: []},
			sheenRamp: {type: "ramp", default: []},
		},
		layerBody: (a, specified, raw)=>{
			const meanE = specified.has("seamRamp") ? meanFadeExpr(raw!.seamRamp, a.seamRamp) : "vec3(0.55)";
			return `
  // Seam lattice along y = down-slope: period = seam spacing across the panel width (x).
  float xw = p.x / max(${a.seamSpacing}, 1e-4);
  float cell = floor(xw);
  float fx = xw - cell;
  // Raised seam == band at the cell boundary.
  float soft = clamp(matPixelSize(), 1e-5, max(${a.seamWidth}, 1e-5));
  float seam = 1.0 - smoothstep(max(${a.seamWidth} - soft, 0.0), ${a.seamWidth} + soft, min(fx, 1.0 - fx));
  float h = seam * ${a.seamHeight};
  // Panel colour with a per-panel tint + a broad sheen (subtle fbm) so the metal isn't flat.
  vec3 panelC = ${specified.has("seamRamp") ? `rampVal(hash01(uvec2(uint(int(cell) * 2654435761), 23u)), ${a.seamRamp})` : "vec3(0.55)"};
  float sheen = ${specified.has("sheenRamp") ? `fbmVal(p, 1.0, 3, 2.3, 0.5, 1) * 0.15` : "0.0"};
  vec3 c = panelC + vec3(sheen);
  float fade = smoothstep(1.0 * matPixelSize(), 2.0 * matPixelSize(), max(${a.seamWidth}, 1e-5));
  c = mix(c, ${meanE}, seam * (1.0 - fade));
  return vec4(c, h);`;
		},
	},
};

export function getPrimitiveDef(kind: PrimitiveKind): PrimitiveDef {
	return PRIMITIVE_REGISTRY[kind];
}

/** Prelude: the shared GLSL functions, emitted once per assembled shader, in dependency order. */
export const PRIMITIVE_PRELUDE = [
	// Order matters: WAVES_GLSL's domainWarpP calls fbmVal; BANDED_NOISE_GLSL also calls fbmVal
	// (must follow it); COVERAGE_FIELD_GLSL calls fbmVal + domainWarpP (must follow both);
	// DOMAIN_REPEAT_GLSL's wood pattern calls wavesVal + valueNoise; MASK_GLSL calls
	// matGeoNormal() (assembler-emitted, above); AMBIENT_DETAIL_GLSL (Set10) calls
	// matPixelSize() + the ambient uniforms + matAmbientSlopeScale() (all assembler-emitted,
	// above) and only composes itself (ambientValueNoise/ambientValCoord/ambientBandFade) — no
	// cross-primitive deps, so it can sit last.
	// MACRO_MOD_GLSL (Option D) calls valueNoise (Fbm.glsl) + matPixelSize(), so it must follow FBM_GLSL; it also
	// DECLARES the `g_macroMod` global the assembler's modulated arg expressions read, so it must precede every
	// layer/deriv/variance wrapper (all emitted after the prelude — satisfied by construction).
	HASH_GLSL, RAMP_GLSL, FBM_GLSL, MACRO_MOD_GLSL, SCATTER_GLSL, WORLEY_GLSL, WAVES_GLSL, BANDED_NOISE_GLSL, COVERAGE_FIELD_GLSL, DOMAIN_REPEAT_GLSL, MASK_GLSL, AMBIENT_DETAIL_GLSL,
].join("\n");

/** Format a number as a GLSL float literal (always carries a decimal point — ES 300 has no int→float coercion). */
export function floatLit(v: number): string {
	if (!isFinite(v)) throw new Error(`[PrimitiveRegistry] non-finite literal value: ${v}`);
	if (Number.isInteger(v) && Math.abs(v) < 1e15) return v.toFixed(1);
	return String(v);
}

/** Format a number as a GLSL int literal. */
export function intLit(v: number): string {
	if (!Number.isInteger(v)) throw new Error(`[PrimitiveRegistry] non-integer int literal: ${v}`);
	return Math.round(v).toString();
}

/** Format a color literal as a GLSL vec3 constructor. */
export function colorLit(c: ParamColor): string {
	return `vec3(${floatLit(c[0])}, ${floatLit(c[1])}, ${floatLit(c[2])})`;
}

/** Pad a ramp stop array to exactly RAMP_MAX_STOPS vec4s (t, r, g, b). Short ramps get trailing
 *  repeats of the last color at t = 1.0 (rampVal terminates there); empty ramps become zeros. */
export function padRampStops(stops: ParamRamp): Array<[number, number, number, number]> {
	if (stops.length % 4 != 0) throw new Error(`[PrimitiveRegistry] ramp stop array must be t,r,g,b quads (got length ${stops.length})`);
	const quads: Array<[number, number, number, number]> = [];
	for (let i = 0; i < stops.length; i += 4) quads.push([stops[i], stops[i + 1], stops[i + 2], stops[i + 3]]);
	if (quads.length > RAMP_MAX_STOPS) throw new Error(`[PrimitiveRegistry] ramp has ${quads.length} stops (max ${RAMP_MAX_STOPS})`);
	if (quads.length == 0) {
		for (let i = 0; i < RAMP_MAX_STOPS; i++) quads.push([0, 0, 0, 0]);
		return quads;
	}
	const last = quads[quads.length - 1];
	while (quads.length < RAMP_MAX_STOPS) quads.push([1.0, last[1], last[2], last[3]]);
	return quads;
}

/** Render a literal ramp as a GLSL expression of type vec4[6]. */
export function rampExpr(stops: ParamRamp): string {
	const padded = padRampStops(stops);
	return `vec4[6](${padded.map(q=>`vec4(${floatLit(q[0])}, ${floatLit(q[1])}, ${floatLit(q[2])}, ${floatLit(q[3])})`).join(", ")})`;
}

/** Render a LITERAL layer arg as a GLSL expression for the given schema type. ParamRefs are the
 *  assembler's job (they need the recipe-id → uniform-name context); calling this on a ref throws. */
export function literalArgExpr(arg: LayerArg, type: ParamKind | "int"): string {
	if (type == "int") {
		if (typeof arg == "object") throw new Error(`[PrimitiveRegistry] int-typed arg cannot be a ParamRef or array (got ${JSON.stringify(arg)})`);
		if (typeof arg != "number") throw new Error(`[PrimitiveRegistry] non-numeric int literal (${JSON.stringify(arg)})`);
		return intLit(arg);
	}
	if (typeof arg == "object") {
		if (Array.isArray(arg)) {
			if (type == "ramp") return rampExpr(arg as ParamRamp);
			if (type == "color") return colorLit(arg as ParamColor);
			if (type == "vec3") return colorLit(arg as ParamColor); // a vec3 is a 3-scalar triple, same GLSL shape
			throw new Error(`[PrimitiveRegistry] array literal given for ${type}-typed arg`);
		}
		throw new Error(`[PrimitiveRegistry] ParamRef "${(arg as {ref: string}).ref}" must be resolved to a uniform name by the assembler`);
	}
	if (type == "ramp" || type == "color" || type == "vec3" || type == "enum" || type == "bool") {
		throw new Error(`[PrimitiveRegistry] non-scalar literal given for ${type}-typed arg`);
	}
	if (typeof arg != "number") throw new Error(`[PrimitiveRegistry] non-numeric literal (${JSON.stringify(arg)}) given for ${type}-typed arg`);
	return floatLit(arg);
}

/** Mean colour of a ramp over t ∈ [0, 1] (Set3 §4.2 — the mean-converging AA fade target).
 *  Computed in JS as the piecewise-linear integral of the ramp's colour over t, so a
 *  sub-pixel fade converges to the TRUE average colour of the ramp (rampVal(0.5, ramp) is
 *  NOT the mean for a non-linear ramp — a t=0.5 sample of a non-linear ramp leaves zoomed-out
 *  surfaces biased, which is exactly the dirt/mulch/wood "zoom-out wash" the plan documents).
 *  Short ramps are padded exactly like padRampStops: trailing repeats of the last colour at
 *  t = 1.0 (a flat tail contributes nothing to the integral).  Empty ramps → mid grey.
 *  `sharpness` (optional) applies a pow(t, k) warping to the t-domain BEFORE the integral —
 *  used by the bandedNoise colour paths whose band value is pow(·, sharpness)-warped (Set3
 *  §4.5: a sharp=1 sinusoid has mean 2/π ≈ 0.637; higher sharpness shifts it).  The mean of
 *  the WARPED ramp is computed via a numerical quadrature (Simpson) over the warped t. */
export function rampMean(ramp: ParamRamp, sharpness = 1): [number, number, number] {
	const quads = padRampStops(ramp);
	if (quads.every(q => q[1] == 0 && q[2] == 0 && q[3] == 0)) return [0.5, 0.5, 0.5];
	if (sharpness == 1) {
		// Exact piecewise-linear integral: area under each segment + the flat tails.
		let area = [0, 0, 0];
		for (let i = 0; i < quads.length - 1; i++) {
			const [t0, r0, g0, b0] = quads[i];
			const [t1, r1, g1, b1] = quads[i + 1];
			if (t1 <= t0) continue;
			for (let c = 0; c < 3; c++) {
				const c0 = [r0, g0, b0][c];
				const c1 = [r1, g1, b1][c];
				area[c] += (t1 - t0) * (c0 + c1) / 2;
			}
		}
		return [area[0], area[1], area[2]];
	}
	// Warped domain: sample colour at t^sharpness (the GLSL pow remap) via Simpson's rule.
	const steps = 32;
	let sum = [0, 0, 0];
	for (let i = 0; i <= steps; i++) {
		const t = i / steps;
		const w = i == 0 || i == steps ? 1 : (i % 2 == 0 ? 2 : 4);
		const wt = Math.pow(t, sharpness);
		// rampVal over the padded stops — reuse the same interpolation the shader uses.
		let r = 0, g = 0, b = 0;
		for (let q = 0; q < quads.length - 1; q++) {
			const [t0, r0, g0, b0] = quads[q];
			const [t1, r1, g1, b1] = quads[q + 1];
			if (wt <= t1 || q == quads.length - 2) {
				const span = t1 - t0;
				const s = span > 1e-5 ? Math.min(Math.max((wt - t0) / span, 0), 1) : 1;
				r = r0 + (r1 - r0) * s;
				g = g0 + (g1 - g0) * s;
				b = b0 + (b1 - b0) * s;
				break;
			}
		}
		sum[0] += w * r;
		sum[1] += w * g;
		sum[2] += w * b;
	}
	const h = 1 / (3 * steps);
	return [sum[0] * h, sum[1] * h, sum[2] * h];
}

/** Format a mean colour as a GLSL vec3 literal (for the Set3 §4.2 mean-converging fades). */
export function meanLit(m: [number, number, number]): string {
	return `vec3(${floatLit(m[0])}, ${floatLit(m[1])}, ${floatLit(m[2])})`;
}

/** GLSL expression for the mean colour of a RAMP ARG (Set3 §4.2).
 *  Literal ramp → the JS-computed mean baked as a literal (deterministic; GPU-parity exact).
 *  ParamRef ramp → `rampMeanOfStops(<uniform>)` computed in-shader from the stops, so the fade
 *  tracks slider changes AND the D10 free-variant property holds (params-only variants share
 *  one program — a JS-baked literal of the DEFAULT would go stale and break the cache key). */
export function meanFadeExpr(rampArg: LayerArg, rampExpr: string, sharpness = 1): string {
	return isParamRef(rampArg)
		? `rampMeanOfStops(${rampExpr})`
		: meanLit(rampMean(rampArg as ParamRamp, sharpness));
}